import { Fetcher } from "../../../Helpers/fetcher";
import { ensureArray } from "../../../Helpers/utils";
// Manage CLI / Manage API CLI / Manage Groups / Manage Locations are deliberately
// NOT multi-select modules -- an ACL only grants the dialer(s) themselves (via
// "Manage Dialers"). CLI numbers, groups and locations are no longer pre-selected
// here; the sub-superadmin who holds this ACL creates/manages their own groups
// and locations directly (scoped to just their own, not superadmin's), so these
// modules just get plain Read/Add/Edit/Delete/View Permission checkboxes like any
// other module with children.
export const MultiSelectModules = new Set([
    "Manage Dialers",
    "Manage Users",
    "Manage Locations",
    "Number Action"
]);

// Extra, non-module options selectable under "Manage Users" — these gate
// the Setting1-4 / Submission checkboxes shown to a user when a
// sub-superadmin (holding this ACL) creates/edits them. Their `id`s are
// checked directly (not resolved against /api/obd/module) — see
// CreateUser.js's isManageUsersOptionAllowed / UpdateUserSetting.js.
export const MANAGE_USERS_SETTING_OPTIONS = [
    { name: "Setting 1", id: "setting1" },
    { name: "Setting 2", id: "setting2" },
    { name: "Setting 3", id: "setting3" },
    { name: "Setting 4", id: "setting4" },
    { name: "Submission", id: "submission" },
];

// Which of Remove DND / Remove Blacklist / Remove Whitelist a sub-superadmin
// (holding this ACL) can use on the "Search DND-Blist-Wlist" page. Static,
// not resolved against any API -- checked directly by `id`, same pattern as
// MANAGE_USERS_SETTING_OPTIONS. See SearchBlacklistDND.js's isNumberActionAllowed.
export const NUMBER_ACTION_OPTIONS = [
    { name: "Remove DND", id: "remove_dnd" },
    { name: "Remove Blacklist", id: "remove_blacklist" },
    { name: "Remove Whitelist", id: "remove_whitelist" },
];


export const moduleOrder = [
    "Manage Dialers",
    "Manage CLI",
    "Manage API CLI",
    "Manage Groups",
    "Manage Users",
    "Voice Plans",
    "TTS Plans",
    "Voice Library",
    "Manage Campaign",
    "Transcription",
    "Manage Api Key",
    "Manage Locations"
];

export const sortModules = (a, b) => {
    const aIndex = moduleOrder.indexOf(a.mname);
    const bIndex = moduleOrder.indexOf(b.mname);

    return (
        (aIndex === -1 ? 999 : aIndex) -
        (bIndex === -1 ? 999 : bIndex)
    );
};

export const hasAnyPermissionSelected = (moduleList, permissions) => {
    return moduleList.some(m => {
        const p = permissions[m.mname] || {};

        const parentSelected =
            p.read || p.add || p.edit || p.delete || p.view_permission;

        const childSelected = m.children?.some(c => {
            const cp = permissions[c.moduleName] || {};
            return (
                cp.read ||
                cp.add ||
                cp.edit ||
                cp.delete ||
                cp.view_permission
            );
        });

        return parentSelected || childSelected;
    });
};

export const buildAclPayload = ({
    moduleList,
    permissions,
    aclName,
    userId,

}) => {
    const finalData = {};

    moduleList.forEach(m => {
        const hasChild = m.children?.length > 0;
        const p = permissions[m.mname] || {};

        if (!hasChild) {
            finalData[m.mname] = { ...p };
        } else {
            finalData[m.mname] = {
                read: p.read || false,
                view_permission: p.view_permission || false,
                children: m.children.map(c => ({
                    [c.moduleName]: permissions[c.moduleName] || {}
                }))
            };
        }
    });

    return {
        moduleOperation: JSON.stringify(finalData),
        aclName,
        userId,
    };
};

export const buildExpandedModulesFromPermissions = (permissions = {}) => {
    const expanded = {};

    Object.entries(permissions).forEach(([moduleName, moduleValue]) => {
        const children = moduleValue?.children || [];

        if (!Array.isArray(children) || children.length === 0) return;

        const shouldExpand = children.some(childObj => {
            const [childName, childPermissions] = Object.entries(childObj)[0] || [];

            if (!childPermissions) return false;

            return Object.values(childPermissions).some(v => v === true);
        });

        if (shouldExpand) {
            expanded[moduleName] = true;
        }
    });

    return expanded;
};

export const parseAclModuleListingData = (data = {}) => {
    try {
        const parsed =
            typeof data === "string"
                ? JSON.parse(data)
                : data || {};

        const restored = {};

        Object.entries(parsed).forEach(([moduleName, value]) => {
            if (!value) return;

            // CASE 1: already array format (your current structure)
            if (Array.isArray(value)) {
                restored[moduleName] = value;
            }

            // CASE 2: old structure with selectedItems
            else if (value?.selectedItems) {
                restored[moduleName] = value.selectedItems;
            }

            // CASE 3: fallback (if backend sends weird format)
            else if (Array.isArray(value?.data)) {
                restored[moduleName] = value.data;
            }
        });

        return restored;

    } catch (err) {
        console.log("parseAclModuleListingData error:", err);
        return {};
    }
};

export const buildModuleListingPayload = (moduleListingData = {}) => {
    const cleanedModuleListingData = Object.fromEntries(
        Object.entries(moduleListingData).map(([key, values]) => [
            key,
            values.map(v => v?.id ?? v)
        ])
    );

    return cleanedModuleListingData;
};